import type { SendEnquiriesPayload } from '@bridebook/web/lib/enquiries/use-send-enquiries-action';
import { isAlreadyContactedResult } from './client-actions/already-contacted';
import { HookActionTypes } from './client-actions/hooks/action-types';
import {
  executeClientActionsInputSchema,
  executeClientActionsOutputSchema,
  type ExecuteClientActionsOutput,
} from './client-actions/schema';
import { isFinished, isToolPartOf, toolParts } from './tool-parts';
import { TOOLS } from './tools/registry';
import type { PlannerUIMessage } from './types';

// A client-actions call with one `sendEnquiries` action only. Other batches keep the consent card.
export const enquiryProposalOf = (input: unknown): SendEnquiriesPayload | null => {
  const parsed = executeClientActionsInputSchema.safeParse(input);
  if (!parsed.success || parsed.data.actions.length !== 1) return null;
  const [action] = parsed.data.actions;
  return action.type === HookActionTypes.SEND_ENQUIRIES ? action.payload : null;
};

// Whether the message holds an enquiry call, whatever came of it.
export const holdsEnquiry = (message: PlannerUIMessage): boolean =>
  toolParts(message, TOOLS.executeClientActions).some(
    (part) => enquiryProposalOf(part.input) !== null,
  );

// The client-action calls in the message still waiting on their card, by call id.
export const openClientActionCalls = (message: PlannerUIMessage): string[] =>
  toolParts(message, TOOLS.executeClientActions)
    .filter((part) => part.state === 'input-available')
    .map((part) => part.toolCallId);

// Request caps on the pending venues. A body over them is rejected.
export const PENDING_VENUES_MAX = 10;
export const PENDING_VENUE_NAME_MAX = 120;

// The venues of proposals the couple has not acted on yet, for the turn note.
export const pendingEnquiryVenues = (
  messages: PlannerUIMessage[],
  isPending: (toolCallId: string) => boolean,
): string[] => {
  const names = messages.flatMap((message) =>
    toolParts(message, TOOLS.executeClientActions)
      .filter((part) => part.state === 'input-available' && isPending(part.toolCallId))
      .flatMap(
        (part) => enquiryProposalOf(part.input)?.suppliers.map((supplier) => supplier.name) ?? [],
      ),
  );
  return [...new Set(names.map((name) => name.slice(0, PENDING_VENUE_NAME_MAX)))].slice(
    0,
    PENDING_VENUES_MAX,
  );
};

/**
 * The venues this thread sent an enquiry to, or has an enquiry card waiting for. A declined
 * card does not count, so the couple can still ask for a brochure later.
 */
export const enquiredVenues = (
  messages: PlannerUIMessage[],
  isDeclined: (toolCallId: string) => boolean,
): SendEnquiriesPayload['suppliers'] =>
  messages.flatMap((message) =>
    toolParts(message, TOOLS.executeClientActions).flatMap((part) => {
      const proposal = enquiryProposalOf(part.input);
      if (!proposal) return [];
      if (part.state === 'input-available') {
        return isDeclined(part.toolCallId) ? [] : proposal.suppliers;
      }
      if (part.state !== 'output-available') return [];
      // A settled card counts when the send went through, or when the couple already have a
      // conversation with the venue
      const output = executeClientActionsOutputSchema.safeParse(part.output);
      const contacted =
        output.success &&
        output.data.results.some(
          (result) =>
            result.type === HookActionTypes.SEND_ENQUIRIES &&
            (result.ok || isAlreadyContactedResult(result)),
        );
      return contacted ? proposal.suppliers : [];
    }),
  );

export const holdsClientActionsCall = (message: PlannerUIMessage, toolCallId: string): boolean =>
  toolParts(message, TOOLS.executeClientActions).some((part) => part.toolCallId === toolCallId);

// Why an enquiry call gets no card: another card in the reply is still open, or the venue
// already had its card in this reply.
export type BlockedEnquiry = 'open' | 'answered';

// A blocked call settles with this flag and its reason, so it never counts as the venue's card.
export const blockedEnquiryData = (reason: BlockedEnquiry) => ({ blocked: true, reason }) as const;

// Why a settled call was blocked, or null when it was not.
const blockedReasonOf = (output: unknown): BlockedEnquiry | null => {
  const parsed = executeClientActionsOutputSchema.safeParse(output);
  if (!parsed.success) return null;
  for (const result of parsed.data.results) {
    const data = result.data as { blocked?: unknown; reason?: unknown } | undefined;
    if (data?.blocked === true) return data.reason === 'open' ? 'open' : 'answered';
  }
  return null;
};

const isBlockedOutput = (output: unknown): boolean => blockedReasonOf(output) !== null;

// The newest message, when it is a coach reply, and its enquiry calls in call order.
const newestReplyEnquiries = (messages: PlannerUIMessage[]) => {
  const reply = messages.at(-1);
  if (reply?.role !== 'assistant') return null;
  const proposals = toolParts(reply, TOOLS.executeClientActions).filter(
    (part) => enquiryProposalOf(part.input) !== null,
  );
  return { reply, proposals };
};

const venueIdsOf = (part: { input?: unknown }): string[] =>
  enquiryProposalOf(part.input)?.suppliers.map(({ id }) => id) ?? [];

/**
 * Whether the newest reply keeps calling an enquiry that was blocked: a venue asked again
 * after its card was answered, or the same venue blocked twice. Each blocked call settles at
 * once and the reply goes on, so without a stop the coach can call again and again. Several
 * venues each blocked once while another card was open are the next cards, not a loop.
 */
export const enquiryRetryLoop = (messages: PlannerUIMessage[]): boolean => {
  const proposals = newestReplyEnquiries(messages)?.proposals ?? [];
  const blockedTimes = new Map<string, number>();
  for (const part of proposals) {
    const reason = isFinished(part) ? blockedReasonOf(part.output) : null;
    if (reason === 'answered') return true;
    if (!reason) continue;
    for (const id of venueIdsOf(part)) {
      const times = (blockedTimes.get(id) ?? 0) + 1;
      if (times >= 2) return true;
      blockedTimes.set(id, times);
    }
  }
  return false;
};

/**
 * The enquiry calls in a reply that get no card, and why. The couple answers one card at a
 * time, and each venue gets one card per reply. A blocked call is not the venue's card, so
 * the venue can still come next.
 */
export const blockedEnquiriesOf = (message: PlannerUIMessage): Map<string, BlockedEnquiry> => {
  const blocked = new Map<string, BlockedEnquiry>();
  const carded = new Set<string>();
  let open = false;
  for (const part of toolParts(message, TOOLS.executeClientActions)) {
    const proposal = enquiryProposalOf(part.input);
    if (!proposal || (isFinished(part) && isBlockedOutput(part.output))) continue;
    const venueIds = proposal.suppliers.map(({ id }) => id);
    // Only a call still waiting can be blocked: a settled one already had its card.
    if (part.state === 'input-available' && (open || venueIds.some((id) => carded.has(id)))) {
      blocked.set(part.toolCallId, open ? 'open' : 'answered');
      continue;
    }
    venueIds.forEach((id) => carded.add(id));
    if (part.state === 'input-available') open = true;
  }
  return blocked;
};

// A lookup's sure match. A near match or a miss names no venue to contact.
const lookedUpVenue = (output: unknown): { id: string; name: string } | null => {
  const result = output as {
    suppliers?: { id: string; name: string }[];
    nearMatch?: unknown;
    unavailable?: unknown;
  };
  if (result.nearMatch || result.unavailable) return null;
  return result.suppliers?.[0] ?? null;
};

/**
 * The next venue to enquire with in this reply, once every enquiry card in it is settled:
 * a venue the reply looked up that has had no card in it yet. Sent, declined and refused
 * cards all count as had. Null while a card is open, or when the reply holds no enquiry.
 */
export const nextEnquiryVenue = (
  messages: PlannerUIMessage[],
): { id: string; name: string } | null => {
  const newest = newestReplyEnquiries(messages);
  if (!newest || newest.proposals.length === 0 || !newest.proposals.every(isFinished)) {
    return null;
  }
  const carded = new Set(
    newest.proposals.filter((part) => !isBlockedOutput(part.output)).flatMap(venueIdsOf),
  );
  return (
    toolParts(newest.reply, TOOLS.searchSupplierByName)
      .filter(isFinished)
      .map((part) => lookedUpVenue(part.output))
      .find((venue) => venue !== null && !carded.has(venue.id)) ?? null
  );
};

// Whether the newest reply holds enquiry cards and every one of them has settled.
export const enquiriesSettled = (messages: PlannerUIMessage[]): boolean => {
  const proposals = newestReplyEnquiries(messages)?.proposals ?? [];
  return proposals.length > 0 && proposals.every(isFinished);
};

/**
 * Whether the newest reply's enquiry cards are all settled and none of them sent: each was
 * declined, refused or blocked. Such a reply still owes the couple a next step.
 */
export const noEnquirySent = (messages: PlannerUIMessage[]): boolean => {
  const proposals = newestReplyEnquiries(messages)?.proposals ?? [];
  if (proposals.length === 0 || !proposals.every(isFinished)) return false;
  return !proposals.some((part) => {
    const output = executeClientActionsOutputSchema.safeParse(part.output);
    return (
      output.success &&
      output.data.results.some(
        (result) => result.type === HookActionTypes.SEND_ENQUIRIES && result.ok,
      )
    );
  });
};

/**
 * Records a client-actions result on the message that holds the call, when that is not the
 * newest message and the chat SDK cannot write it. Other messages keep their identity.
 */
export const withClientActionsOutput = (
  messages: PlannerUIMessage[],
  toolCallId: string,
  output: ExecuteClientActionsOutput,
): PlannerUIMessage[] =>
  messages.some((message) => holdsClientActionsCall(message, toolCallId))
    ? messages.map((message) =>
        holdsClientActionsCall(message, toolCallId)
          ? ({
              ...message,
              parts: message.parts.map((part) =>
                isToolPartOf(part, TOOLS.executeClientActions) && part.toolCallId === toolCallId
                  ? { ...part, state: 'output-available', output }
                  : part,
              ),
            } as PlannerUIMessage)
          : message,
      )
    : messages;
